// src/components/ScaffoldingCoach.jsx
import React, { useState } from "react";
import "../styles/scaffoldingCoach.css";
import { FiChevronDown, FiChevronUp, FiCheckCircle, FiStar, FiZap, FiThumbsUp } from "react-icons/fi";

/**
 * ScaffoldingCoach - Displays adaptive coaching tips for Condition B students
 * 
 * Design Goals:
 * - Engaging: Modern, colorful design to catch attention
 * - Progressive disclosure: Show only what's needed when needed
 * - Motivating: Positive reinforcement for good prompts
 */

// Category display name mapping
const CATEGORY_DISPLAY_NAMES = {
  specificity: "Be Specific",
  examples: "Show Examples",
  success_criteria: "Success Criteria",
  role: "Assign Role",
  explanations: "Request Reasoning",
  follow_up: "Refine Iteratively",
  personalization: "Add Context",
};

const ScaffoldingCoach = ({ scaffolding }) => {
  const [tipsExpanded, setTipsExpanded] = useState(true);  // Expanded by default - show tips first
  const [reflectionExpanded, setReflectionExpanded] = useState(true);  // Expanded by default - important for students to see

  if (!scaffolding) {
    return null;
  }

  const { suggestions = [], reflection_prompts = [], categories = [], quality_score = 0.5, praise = null } = scaffolding;

  // Don't show anything if there's no content
  const hasSuggestions = suggestions.length > 0;
  const hasReflections = reflection_prompts.length > 0;
  const hasPraise = praise && praise.trim().length > 0;

  if (!hasSuggestions && !hasReflections && !hasPraise) {
    return null;
  }

  // Determine if this is a praise scenario (high quality prompt)
  const isHighQuality = quality_score >= 0.7;

  return (
    <div className="scaffolding-coach">
      {/* Tips Section - Show at top, expanded by default (only if there are suggestions) */}
      {hasSuggestions && (
        <div className="coach-section tips-section">
          <button
            className="coach-header"
            onClick={() => setTipsExpanded(!tipsExpanded)}
            aria-expanded={tipsExpanded}
          >
            <span className="coach-icon">
              {isHighQuality ? <FiStar /> : <FiZap />}
            </span>
            <span className="coach-title">
              {isHighQuality ? "Excellent Prompt! 🌟" : "💡 Prompt Tips"}
            </span>
            {categories.length > 0 && (
              <span className="categories">
                {categories.slice(0, 2).map((cat, idx) => (
                  <span key={idx} className="category-badge">
                    {CATEGORY_DISPLAY_NAMES[cat] || cat}
                  </span>
                ))}
              </span>
            )}
            <span className="expand-icon">
              {tipsExpanded ? <FiChevronUp /> : <FiChevronDown />}
            </span>
          </button>
          
          {tipsExpanded && (
            <div className="coach-content">
              {hasPraise && (
                <div className="praise-banner">
                  <span className="praise-icon">✨</span>
                  <span className="praise-text">{praise}</span>
                </div>
              )}
              <ul className="tips-list">
                {suggestions.map((tip, idx) => (
                  <li key={idx}>{tip}</li>
                ))}
              </ul>
              {!hasPraise && (
                <p className="coach-note">
                  💡 Pro Tip: More specific prompts lead to better, more tailored answers!
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reflection Section - Show at bottom, expanded by default */}
      {hasReflections && (
        <div className="coach-section reflection-section">
          <button
            className="coach-header"
            onClick={() => setReflectionExpanded(!reflectionExpanded)}
            aria-expanded={reflectionExpanded}
          >
            <span className="coach-icon"><FiThumbsUp /></span>
            <span className="coach-title">✓ Evaluate This Answer</span>
            <span className="expand-icon">
              {reflectionExpanded ? <FiChevronUp /> : <FiChevronDown />}
            </span>
          </button>
          
          {reflectionExpanded && (
            <div className="coach-content">
              <ul className="reflection-list">
                {reflection_prompts.map((prompt, idx) => (
                  <li key={idx}>
                    <FiCheckCircle className="check-icon" />
                    {prompt}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScaffoldingCoach;
